import React, { useEffect, useState } from 'react';

const ProgressRing = ({ 
  progress = 0, 
  size = 60, 
  strokeWidth = 4,
  color = '#10B981',
  backgroundColor = '#E5E7EB',
  animated = true,
  showPercentage = false,
  duration = 1000
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  
  // Calculate dimensions
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (animatedProgress / 100) * circumference;

  // Animate progress
  useEffect(() => {
    if (!animated) {
      setAnimatedProgress(progress);
      return;
    }

    const startProgress = animatedProgress;
    const targetProgress = Math.min(100, Math.max(0, progress));
    const difference = targetProgress - startProgress;
    
    if (Math.abs(difference) < 0.1) return;

    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progressRatio, 3);
      
      const currentProgress = startProgress + (difference * easeOut);
      setAnimatedProgress(currentProgress);
      
      if (progressRatio < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [progress, animated, duration]);

  return (
    <div className="progress-ring" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="progress-ring-svg"
        style={{ transform: 'rotate(-90deg)' }}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          className="progress-ring-background"
        />
        
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={`progress-ring-progress ${animated ? 'animated' : ''}`}
          style={{
            transition: animated ? `stroke-dashoffset ${duration}ms ease-out` : 'none'
          }}
        />
        
        {/* Glow effect for high progress */}
        {animatedProgress > 75 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="progress-ring-glow"
            style={{
              opacity: (animatedProgress - 75) / 25 * 0.3,
              filter: 'blur(2px)'
            }}
          />
        )}
      </svg>
      
      {/* Center content */}
      <div className="progress-ring-content">
        {showPercentage ? (
          <div className="progress-percentage">
            <span className="percentage-number">{Math.round(animatedProgress)}</span>
            <span className="percentage-symbol">%</span>
          </div>
        ) : (
          <div className="progress-icon">
            {animatedProgress >= 100 ? '✓' : ''}
          </div>
        )}
      </div>
      
      {/* Celebration sparkles for completion */}
      {animatedProgress >= 100 && (
        <div className="progress-sparkles">
          <div className="sparkle sparkle-1">✨</div>
          <div className="sparkle sparkle-2">⭐</div>
          <div className="sparkle sparkle-3">🎉</div>
          <div className="sparkle sparkle-4">✨</div>
        </div>
      )}
    </div>
  );
};

export default ProgressRing;